import { CustomReport } from '@principle-theorem/principle-core';
import {
  IBaseChartBuilderConfig,
  ICustomChartSettings,
  ICustomReportChart,
  MeasureReducer,
} from '@principle-theorem/principle-core/interfaces';
import {
  ICanGroupMeasuresProperty,
  IChartFilter,
  IMeasureBuilderData,
  IReportBuilderDataSource,
} from '@principle-theorem/reporting/interfaces';
import { compact } from 'lodash';
import { v4 as uid } from 'uuid';
import { CanGroupMeasuresProperty } from '../../models/measure-properties';
import { ReportBuilderDataSource } from '../report-builder-data-source';
import {
  ChartFilterDefinition,
  IChartFilterDefinition,
} from './chart-filter-definition';

export class ReportBuilderCharts {
  static predefined<
    BuilderConfig extends IBaseChartBuilderConfig = IBaseChartBuilderConfig,
  >(
    id: string,
    groupBy: CanGroupMeasuresProperty,
    filterDefinition: IChartFilterDefinition<BuilderConfig>
  ): IChartFilter {
    return {
      id,
      chart: this.toCustomReportChart(groupBy, filterDefinition),
      groupBy,
      availableInEditMode: true,
    };
  }

  static toCustomReportChart<
    BuilderConfig extends IBaseChartBuilderConfig = IBaseChartBuilderConfig,
  >(
    groupBy: CanGroupMeasuresProperty,
    filterDefinition: IChartFilterDefinition<BuilderConfig>
  ): ICustomReportChart<ICustomChartSettings, BuilderConfig> {
    const { config, builderConfig } = filterDefinition;
    const builderData = filterDefinition.config.builderData;
    return {
      uid: uid(),
      type: config.type,
      label: config.labels.title,
      groupBy: groupBy.metadata.id,
      settings: builderData.measures.map((measure) => ({
        uid: uid(),
        label: measure.label ?? measure.measure.metadata.label,
        dataPoint: measure.measure.metadata.id,
        reducer: measure.reducer || MeasureReducer.Sum,
        filters: measure.filters,
      })),
      builderData: {
        plottedOverTime: builderData.plottedOverTime,
        accumulateOverTime: builderData.accumulateOverTime,
        colourOverride: builderData.colourOverride,
        groupLimit: builderData.groupLimit,
        otherGroupLabel: builderData.otherGroupLabel,
      },
      builderConfig,
    };
  }

  static gridSpan(
    multiplier: number,
    singleChartWidth = 260,
    gapBetweenCharts = 42
  ): number {
    const chartWidthSum = singleChartWidth * multiplier;
    const gapSum = gapBetweenCharts * (multiplier - 1);
    return chartWidthSum + gapSum;
  }

  static getFilterDefinition(
    dataSource: IReportBuilderDataSource,
    chart: ICustomReportChart
  ): IChartFilterDefinition | undefined {
    const groupBy = this.resolveGroupByMeasure(dataSource, chart);
    if (!groupBy) {
      return;
    }
    const measures = this.resolveMeasures(dataSource, chart);
    const config = ChartFilterDefinition.fromChart(chart, groupBy, measures);
    if (!config) {
      return;
    }
    return {
      config: config.config,
      builderConfig: config.builderConfig,
    };
  }

  static resolveGroupByMeasure(
    dataSource: IReportBuilderDataSource,
    chart: ICustomReportChart
  ): ICanGroupMeasuresProperty | undefined {
    const chartGroupBy = dataSource.groupByOptions
      .flatMap((section) => section.options)
      .find((item) => item.measure.metadata.id === chart.groupBy);
    if (!chartGroupBy) {
      // eslint-disable-next-line no-console
      console.error(
        `ChartBuilder.resolveChartGroupByMeasure - no chartGroupBy for ${dataSource.id} - ${chart.groupBy}`
      );
      return;
    }
    return chartGroupBy.measure;
  }

  static resolveMeasures(
    dataSource: IReportBuilderDataSource,
    chart: ICustomReportChart
  ): IMeasureBuilderData[] {
    const measures = compact(
      chart.settings.flatMap((setting) => {
        const measure = ReportBuilderDataSource.findColumn(
          dataSource,
          setting.dataPoint
        )?.measure;
        if (!measure) {
          if (dataSource.factTable.count.metadata.id === setting.dataPoint) {
            return {
              uid: setting.uid,
              measure: CustomReport.definedColumn(
                dataSource.factTable.count,
                dataSource.factTable.count.metadata.id,
                dataSource.factTable.count.metadata.label
              ).measure,
              reducer: setting.reducer,
              filters: setting.filters,
              label: setting.label,
            };
          }
          return;
        }

        return {
          uid: setting.uid,
          measure,
          reducer: setting.reducer,
          filters: setting.filters,
          label: setting.label,
        };
      })
    );

    if (!measures.length) {
      // eslint-disable-next-line no-console
      console.error(
        `ChartBuilder.resolveChartMeasures for ${dataSource.id} - measures`,
        measures,
        chart
      );
    }
    return measures;
  }
}
